import * as React from "react";
import { observer } from "mobx-react";
import { NewShiftsDialog } from "../../common/NewShiftsDialog/NewShiftsDialog";
import StringsStore from "sh-strings/store";
import { IMsTeamWithScheduleInfo } from "sh-models";
import { TeamSetupMode } from "../store/schema/TeamSetupPickerStore";
import { MSTeamPickerPaneStore } from "../msteampicker/store/schema/MSTeamPickerPaneStore";
import TeamSetupPickerUtils from "../TeamSetupPickerUtils";
import { setShowStepIndicator } from "../store/store";
import { fireAccessibilityAlert } from "sh-application/components/accessibilityAlert";
import Spinner, { SpinnerSize } from "sh-application/components/common/Spinner";
import TeamListItem from "./TeamListItem";
import { Link } from "@fluentui/react-components";
import { NewShiftsDialogForDeleteRestoreTeam } from "../../common/NewShiftsDialog/NewShiftsDialogForDeleteRestoreTeam";

const styles = require("./CreateRestoreScheduleDialog.scss");
const emptyMessageImage = require("sh-assets/images/optimized-svg/Shifts_EmptyState_desktop_NoSchedules.svg");

export interface CreateRestoreScheduleDialogProps {
    instrumentationEventName: string;
    onTeamSelected: (teamWithScheduleInfo: IMsTeamWithScheduleInfo, action: TeamSetupMode) => void;
    loadInitialData: () => Promise<void>;
    autofocus?: boolean;
    onCancel: () => void;
    isOpen: boolean;
    store: MSTeamPickerPaneStore;
}

/**
 * Dialog component for creating or restoring team schedules based on Figma design.
 * This is the new UI that replaces MSTeamPickerPane when DeleteScheduleEnabled flag is on.
 */
@observer
export default class CreateRestoreScheduleDialog extends React.Component<CreateRestoreScheduleDialogProps, {}> {
    private _strings: Map<string, string>;
    private _firstSelectableItemRef = React.createRef<HTMLButtonElement>();

    constructor(props: CreateRestoreScheduleDialogProps) {
        super(props);
        this._strings = StringsStore().registeredStringModules.get("msTeamPicker").strings;
    }

    async componentDidMount() {
        await this.props.loadInitialData();

        // Fire accessibility alert
        fireAccessibilityAlert(this._strings.get("msTeamPickerDialogAriaAlert"));

        if (this.props.autofocus && this._firstSelectableItemRef && this._firstSelectableItemRef.current) {
            this._firstSelectableItemRef.current.focus();
        }
    }

    /**
     * Returns true if the teams data is currently being fetched
     */
    private isTeamsDataLoading(): boolean {
        return this.props.store.isDataLoading;
    }

    /**
     * Handler for when a team is selected
     */
    private handleTeamSelected = (teamWithScheduleInfo: IMsTeamWithScheduleInfo, setupMode: TeamSetupMode) => {
        if (this.props.onTeamSelected) {
            this.props.onTeamSelected(teamWithScheduleInfo, setupMode);
        }
    }

    /**
     * Renders loading progress state
     */
    private renderLoadingProgress(): JSX.Element {
        return (
            <div className={styles.progressIndicatorContainer}>
                <Spinner className={styles.spinner} size={SpinnerSize.small} label={this._strings.get("loadingTeamsMessage")} />
            </div>
        );
    }

    /**
     * Render empty state when no teams are available
     */
    private renderEmptyTeamListState(): JSX.Element {
        return (
            <div className={styles.emptyStateContainer} aria-live="polite">
                <img alt="" className={styles.messageImage} src={emptyMessageImage} />
                <div className={styles.title}>{this._strings.get("noTeamsToCreateScheduleTitle")}</div>
                <div className={styles.subtitle}>{this._strings.get("noTeamsToCreateScheduleSubtitle")}</div>
            </div>
        );
    }

    /**
     * Render the team list
     */
    private renderTeamList(msTeamsWithScheduleInfo: IMsTeamWithScheduleInfo[]): JSX.Element {
        const teamListItems: JSX.Element[] = [];
        let foundFirstSelectableItem = false;

        msTeamsWithScheduleInfo.forEach((teamWithScheduleInfo, index) => {
            const isDisabled = !TeamSetupPickerUtils.isUserTeamAdmin(teamWithScheduleInfo.msTeamInfo);
            let isFirstSelectableItem = false;

            if (!isDisabled && !foundFirstSelectableItem) {
                isFirstSelectableItem = true;
                foundFirstSelectableItem = true;
            }

            teamListItems.push(
                <TeamListItem
                    key={index}
                    teamWithScheduleInfo={teamWithScheduleInfo}
                    isDisabled={isDisabled}
                    onClick={this.handleTeamSelected}
                    isFirstItem={isFirstSelectableItem}
                    buttonRef={isFirstSelectableItem ? this._firstSelectableItemRef : undefined}
                />
            );
        });

        return (
            <div className={styles.teamListContainer} role="list">
                {teamListItems}
            </div>
        );
    }

    /**
     * Render the dialog content
     */
    private renderDialogContent(): JSX.Element {
        const msTeamsWithoutActiveSchedules = this.props.store.msTeamsWithoutActiveSchedules;
        let hasSchedulesToCreate = false;
        const hasTeams = msTeamsWithoutActiveSchedules && msTeamsWithoutActiveSchedules.length > 0;

        if (hasTeams) {
            for (let i = 0; i < msTeamsWithoutActiveSchedules.length; i++) {
                if (TeamSetupPickerUtils.isUserTeamAdmin(msTeamsWithoutActiveSchedules[i].msTeamInfo)) {
                    hasSchedulesToCreate = true;
                    setShowStepIndicator(true);
                    break;
                }
            }
        }

        if (this.isTeamsDataLoading()) {
            return this.renderLoadingProgress();
        }

        if (hasTeams && hasSchedulesToCreate) {
            return this.renderTeamList(msTeamsWithoutActiveSchedules);
        }

        return this.renderEmptyTeamListState();
    }

    /**
     * Render the footer with "Learn more" link
     */
    private renderFooter(): JSX.Element {
        const learnMoreUrl = "https://support.microsoft.com/office/get-started-in-shifts-5f3e30d8-1821-4904-be26-c3cd25a497d6";

        return (
            <div className={styles.footer}>
                <Link href={learnMoreUrl} target="_blank" rel="noopener noreferrer">
                    {this._strings.get("learnMoreAboutCreatingSchedule") || "Learn more about creating a schedule"}
                </Link>
            </div>
        );
    }

    render() {
        const dialogTitle = this._strings.get("msTeamPickerTitle");
        const dialogSubtext = this._strings.get("msTeamPickerDescription");

        return (
            <NewShiftsDialogForDeleteRestoreTeam
                title={dialogTitle}
                subtext={dialogSubtext}
                content={this.renderDialogContent()}
                actions={this.renderFooter()}
                isOpen={this.props.isOpen}
                onDismiss={this.props.onCancel}
                showDismiss={true}
                modalType="modal"
                ariaLabel={dialogTitle}
                customStyles={{
                    dialogSurface: styles.dialogSurface,
                    dialogBody: styles.dialogBody,
                    dialogContent: styles.dialogContent,
                    dialogActions: styles.dialogActions,
                    dialogSubtext: styles.dialogSubtext,
                    dialogBodyContent: styles.dialogBodyContent
                }}
            />
        );
    }
}
